import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  useMediaQuery,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  Skeleton,
  Avatar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  Cancel,
  Pending,
  BarChart,
  ShowChart,
  PieChart,
  Map,
  Person,
  AttachMoney,
  Inventory,
  ShoppingCart,
  Receipt,
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import ZatcaPhase from '../components/ZatcaPhase';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Lottie from 'lottie-react';
import loadingAnimation from '../assets/loading-animation.json';
import errorAnimation from '../assets/Error404.json';
import image1 from '../assets/riyal.png'

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Modern dashboard component matching Valex design
const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [trendsPeriod, setTrendsPeriod] = useState('7');
  const [trendsAnchorEl, setTrendsAnchorEl] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsData, setTrendsData] = useState([]);
  const [summaryPeriod, setSummaryPeriod] = useState('7');
  const [summaryAnchorEl, setSummaryAnchorEl] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/stats');
        setDashboardData(response.data);
        setTrendsData(response.data.dailyTrends || []);
        setSummaryData(response.data.transactionSummary || null);
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch daily trends based on period
  const fetchDailyTrends = async (period) => {
    try {
      setTrendsLoading(true);
      const response = await api.get(`/dashboard/daily-trends?period=${period}`);
      setTrendsData(response.data.dailyTrends);
      setTrendsPeriod(period);
    } catch (err) {
      console.error('Error fetching daily trends:', err);
    } finally {
      setTrendsLoading(false);
    }
  };

  // Handle menu open/close
  const handleTrendsMenuClick = (event) => {
    setTrendsAnchorEl(event.currentTarget);
  };

  const handleTrendsMenuClose = () => {
    setTrendsAnchorEl(null);
  };

  const handlePeriodChange = (period) => {
    fetchDailyTrends(period);
    handleTrendsMenuClose();
  };

  // Fetch transaction summary based on period
  const fetchTransactionSummary = async (period) => {
    try {
      setSummaryLoading(true);
      const response = await api.get(`/dashboard/transaction-summary?period=${period}`);
      setSummaryData(response.data.transactionSummary);
      setSummaryPeriod(period);
    } catch (err) {
      console.error('Error fetching transaction summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Handle summary menu open/close
  const handleSummaryMenuClick = (event) => {
    setSummaryAnchorEl(event.currentTarget);
  };

  const handleSummaryMenuClose = () => {
    setSummaryAnchorEl(null);
  };

  const handleSummaryPeriodChange = (period) => {
    fetchTransactionSummary(period);
    handleSummaryMenuClose();
  };

  // Helper function to get card color based on trend and type
  const getCardColor = (trend, percentage, isSales = true) => {
    const percentageValue = parseFloat(percentage);

    // For sales metrics, up is good (green), down is bad (red)
    // For purchase metrics, down is good (green), up is warning/danger (yellow/red)
    if (isSales) {
      if (trend === 'up') {
        return percentageValue > 20 ? '#38cb89' : '#4ec2f0'; // High growth: green, Normal growth: light blue
      } else {
        return percentageValue > 20 ? '#ff5b5b' : '#ffab00'; // High drop: red, Normal drop: orange
      }
    } else {
      // For purchases, inverse logic (lower is better)
      if (trend === 'down') {
        return percentageValue > 20 ? '#38cb89' : '#4ec2f0'; // High reduction: green, Normal reduction: light blue
      } else {
        return percentageValue > 20 ? '#ff5b5b' : '#ffab00'; // High increase: red, Normal increase: orange
      }
    }
  };

  // Sales metrics data from API
  const salesMetrics = dashboardData ? [
    {
      title: 'TODAY SALES',
      value: dashboardData.salesMetrics.todaySales.value,
      comparison: dashboardData.salesMetrics.todaySales.comparison,
      trend: dashboardData.salesMetrics.todaySales.trend,
      percentage: dashboardData.salesMetrics.todaySales.percentage,
      bgColor: getCardColor(dashboardData.salesMetrics.todaySales.trend, dashboardData.salesMetrics.todaySales.percentage, true)
    },
    {
      title: 'WEEKLY SALES',
      value: dashboardData.salesMetrics.weeklySales.value,
      comparison: dashboardData.salesMetrics.weeklySales.comparison,
      trend: dashboardData.salesMetrics.weeklySales.trend,
      percentage: dashboardData.salesMetrics.weeklySales.percentage,
      bgColor: getCardColor(dashboardData.salesMetrics.weeklySales.trend, dashboardData.salesMetrics.weeklySales.percentage, true)
    },
    {
      title: 'MONTHLY SALES',
      value: dashboardData.salesMetrics.monthlySales.value,
      comparison: dashboardData.salesMetrics.monthlySales.comparison,
      trend: dashboardData.salesMetrics.monthlySales.trend,
      percentage: dashboardData.salesMetrics.monthlySales.percentage,
      bgColor: getCardColor(dashboardData.salesMetrics.monthlySales.trend, dashboardData.salesMetrics.monthlySales.percentage, true)
    },
    {
      title: 'TODAY PURCHASE',
      value: dashboardData.salesMetrics.todayPurchase.value,
      comparison: dashboardData.salesMetrics.todayPurchase.comparison,
      trend: dashboardData.salesMetrics.todayPurchase.trend,
      percentage: dashboardData.salesMetrics.todayPurchase.percentage,
      bgColor: getCardColor(dashboardData.salesMetrics.todayPurchase.trend, dashboardData.salesMetrics.todayPurchase.percentage, false)
    },
    {
      title: 'MONTHLY PURCHASE',
      value: dashboardData.salesMetrics.monthlyPurchase.value,
      comparison: dashboardData.salesMetrics.monthlyPurchase.comparison,
      trend: dashboardData.salesMetrics.monthlyPurchase.trend,
      percentage: dashboardData.salesMetrics.monthlyPurchase.percentage,
      bgColor: getCardColor(dashboardData.salesMetrics.monthlyPurchase.trend, dashboardData.salesMetrics.monthlyPurchase.percentage, false)
    },
  ] : [];

  // Order status data from API
  const orderStatusData = dashboardData ? {
    success: dashboardData.vatOrderStatus.totalSuccess,
    pending: dashboardData.vatOrderStatus.totalPending,
    failed: dashboardData.vatOrderStatus.totalFailed,
  } : { success: 0, pending: 0, failed: 0 };

  // Monthly sales data for bar chart from API
  const monthlyData = dashboardData ? {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Success',
        data: [
          dashboardData.vatOrderStatus.month.jan.success,
          dashboardData.vatOrderStatus.month.feb.success,
          dashboardData.vatOrderStatus.month.mar.success,
          dashboardData.vatOrderStatus.month.apr.success,
          dashboardData.vatOrderStatus.month.may.success,
          dashboardData.vatOrderStatus.month.jun.success,
          dashboardData.vatOrderStatus.month.jul.success,
          dashboardData.vatOrderStatus.month.aug.success,
          dashboardData.vatOrderStatus.month.sep.success,
          dashboardData.vatOrderStatus.month.oct.success,
          dashboardData.vatOrderStatus.month.nov.success,
          dashboardData.vatOrderStatus.month.dec.success,
        ],
        backgroundColor: '#3e80ff',
      },
      {
        label: 'Pending',
        data: [
          dashboardData.vatOrderStatus.month.jan.pending,
          dashboardData.vatOrderStatus.month.feb.pending,
          dashboardData.vatOrderStatus.month.mar.pending,
          dashboardData.vatOrderStatus.month.apr.pending,
          dashboardData.vatOrderStatus.month.may.pending,
          dashboardData.vatOrderStatus.month.jun.pending,
          dashboardData.vatOrderStatus.month.jul.pending,
          dashboardData.vatOrderStatus.month.aug.pending,
          dashboardData.vatOrderStatus.month.sep.pending,
          dashboardData.vatOrderStatus.month.oct.pending,
          dashboardData.vatOrderStatus.month.nov.pending,
          dashboardData.vatOrderStatus.month.dec.pending,
        ],
        backgroundColor: '#ffab00',
      },
      {
        label: 'Failed',
        data: [
          dashboardData.vatOrderStatus.month.jan.failed,
          dashboardData.vatOrderStatus.month.feb.failed,
          dashboardData.vatOrderStatus.month.mar.failed,
          dashboardData.vatOrderStatus.month.apr.failed,
          dashboardData.vatOrderStatus.month.may.failed,
          dashboardData.vatOrderStatus.month.jun.failed,
          dashboardData.vatOrderStatus.month.jul.failed,
          dashboardData.vatOrderStatus.month.aug.failed,
          dashboardData.vatOrderStatus.month.sep.failed,
          dashboardData.vatOrderStatus.month.oct.failed,
          dashboardData.vatOrderStatus.month.nov.failed,
          dashboardData.vatOrderStatus.month.dec.failed,
        ],
        backgroundColor: '#ff5b5b',
      },
    ],
  } : { labels: [], datasets: [] };

  // Recent sales data from API
  const recentSales = dashboardData ? dashboardData.recentSales.map(sale => ({
    id: sale.id,
    customerName: sale.customerName,
    date: new Date(sale.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    amount: sale.amount,
    paymentStatus: sale.paymentStatus,
    status: sale.status,
    color: sale.paymentStatus === 'paid' ? '#38cb89' : sale.paymentStatus === 'pending' ? '#ffab00' : '#ff5b5b'
  })) : [];

  // Sales activity data from API
  const salesActivity = dashboardData ? dashboardData.salesActivity : [];

  // Recent orders data from API
  const recentOrders = dashboardData ? dashboardData.recentOrders : {
    delivered: 0,
    cancelled: 0,
    deliveredPercentage: 0,
  };

  // Prepare data for line chart using trendsData
  const dailyTrendsChartData = {
    labels: trendsData.map(day => day.date),
    datasets: [
      {
        label: 'Sales',
        data: trendsData.map(day => day.sales),
        borderColor: '#3e80ff',
        backgroundColor: 'rgba(62, 128, 255, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#3e80ff',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: 'Purchases',
        data: trendsData.map(day => day.purchases),
        borderColor: '#38cb89',
        backgroundColor: 'rgba(56, 203, 137, 0.1)',
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#38cb89',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  // Top products data from API
  const topProducts = dashboardData ? dashboardData.topProducts.map(product => ({
    productCode: product.product,
    productName: product.productName,
    group: product.group,
    revenue: product.revenue.toFixed(2),
    quantity: product.quantity
  })) : [];

  // Recent earnings data from API
  const recentEarnings = dashboardData ? dashboardData.recentEarnings : [];

  // Transaction summary - use summaryData state instead of dashboardData
  const transactionSummary = summaryData;

  // Calculate percentages for progress bars
  const getPercentage = (value, total) => {
    return total > 0 ? (value / total) * 100 : 0;
  };

  const handleUpgrade = () => {
    navigate('/zatca-onboarding');
  };

  // Skeleton Loading Component with YouTube-style shimmer animation
  const DashboardSkeleton = () => (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa',
        py: 2,
        px: { xs: 1, md: 2 },
        width: '100%',
        overflow: 'auto',
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '-1000px 0',
          },
          '100%': {
            backgroundPosition: '1000px 0',
          },
        },
      }}
    >
      <Container maxWidth="xl">
        {/* Header Skeleton */}
        <Box sx={{ mb: 3 }}>
          <Skeleton
            variant="text"
            width={200}
            height={40}
            animation="wave"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.08)',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
              }
            }}
          />
          <Skeleton
            variant="text"
            width={300}
            height={24}
            animation="wave"
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.08)',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
              }
            }}
          />
        </Box>

        {/* Stats Cards Skeleton */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Skeleton
                      variant="circular"
                      width={48}
                      height={48}
                      animation="wave"
                      sx={{
                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                      }}
                    />
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={24}
                      animation="wave"
                      sx={{
                        borderRadius: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.08)',
                      }}
                    />
                  </Box>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    animation="wave"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    height={20}
                    animation="wave"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Charts Skeleton */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <CardHeader
                title={
                  <Skeleton
                    variant="text"
                    width={150}
                    animation="wave"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                }
                action={
                  <Skeleton
                    variant="rectangular"
                    width={80}
                    height={32}
                    animation="wave"
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                }
              />
              <CardContent>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  animation="wave"
                  sx={{
                    borderRadius: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <CardHeader
                title={
                  <Skeleton
                    variant="text"
                    width={150}
                    animation="wave"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.08)',
                    }}
                  />
                }
              />
              <CardContent>
                <Skeleton
                  variant="circular"
                  width={200}
                  height={200}
                  animation="wave"
                  sx={{
                    mx: 'auto',
                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table Skeleton */}
        <Card
          sx={{
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title={
              <Skeleton
                variant="text"
                width={200}
                animation="wave"
                sx={{
                  bgcolor: 'rgba(0, 0, 0, 0.08)',
                }}
              />
            }
          />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {[1, 2, 3, 4, 5].map((col) => (
                      <TableCell key={col}>
                        <Skeleton
                          variant="text"
                          width="80%"
                          animation="wave"
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.08)',
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((row) => (
                    <TableRow key={row}>
                      {[1, 2, 3, 4, 5].map((col) => (
                        <TableCell key={col}>
                          <Skeleton
                            variant="text"
                            width="90%"
                            animation="wave"
                            sx={{
                              bgcolor: 'rgba(0, 0, 0, 0.08)',
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );

  // Loading state - Show skeleton instead of animation
  if (loading) {
    return <DashboardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
        }}
      >
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Lottie
            animationData={errorAnimation}
            loop={true}
            style={{ width: 300, height: 300, margin: '0 auto' }}
          />
          <Typography variant="body1" sx={{ color: '#8597ac', mt: 2 }}>
            {error}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa',
        py: 2,
        px: { xs: 1, md: 2 },
        width: '100%',
        overflow: 'auto',
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2 }, width: '100%' }}>
        {/* Dashboard Header */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography
              variant="h5"
              component="h1"
              fontWeight="600"
              sx={{
                color: '#031b4e',
                mb: 0.5,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}
            >
              Hi, welcome back!
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Here is your Sales monitoring dashboard
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Removed Online Sales and Offline Sales boxes */}
            </Box>
          </Box>
          <ZatcaPhase
            zatcaStatus={dashboardData?.zatcaStatus}
            onUpgrade={handleUpgrade}
          />
        </Box>

        {/* Metrics Cards Section */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {salesMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={index}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: '4px',
                  overflow: 'hidden',
                  bgcolor: metric.bgColor,
                  color: 'white',
                  boxShadow: 'none',
                  height: '100%',
                  maxHeight: 120,
                }}
              >
                <CardContent sx={{ p: 2, pb: 1, height: '100%', position: 'relative' }}>
                  <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 500, mb: 0.25, textTransform: 'uppercase' }}>
                      {metric.title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', mb: 0.25, fontSize: '1.25rem' }}>
                      {metric.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {metric.trend === 'up' ?
                        <ArrowUpward sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)' }} /> :
                        <ArrowDownward sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.9)' }} />}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                        {metric.percentage} {metric.comparison}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '40%',
                    zIndex: 1,
                    overflow: 'hidden'
                  }}>
                    <Box
                      component="img"
                      src={`data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 25'><path d='M0,25 L20,15 L40,20 L60,5 L80,15 L100,0 L100,25 L0,25 Z' fill='rgba(255,255,255,0.2)' /></svg>`}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Order Status and Transaction Summary Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="SALES & PURCHASE TRENDS"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                action={
                  <>
                    <IconButton size="small" onClick={handleTrendsMenuClick}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={trendsAnchorEl}
                      open={Boolean(trendsAnchorEl)}
                      onClose={handleTrendsMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem
                        onClick={() => handlePeriodChange('7')}
                        selected={trendsPeriod === '7'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        7 Days
                      </MenuItem>
                      <MenuItem
                        onClick={() => handlePeriodChange('14')}
                        selected={trendsPeriod === '14'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        14 Days
                      </MenuItem>
                      <MenuItem
                        onClick={() => handlePeriodChange('30')}
                        selected={trendsPeriod === '30'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        1 Month
                      </MenuItem>
                    </Menu>
                  </>
                }
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 2.5 }}>
                  Daily sales and purchase trends over the last {trendsPeriod === '7' ? '7 days' : trendsPeriod === '14' ? '14 days' : '30 days'}
                </Typography>

                {trendsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : (
                  <>
                    {/* Summary Stats */}
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(62, 128, 255, 0.08)', borderRadius: 1, border: '1px solid rgba(62, 128, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3e80ff' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Total Sales
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {trendsData.reduce((sum, day) => sum + day.sales, 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                            {trendsData.reduce((sum, day) => sum + day.salesCount, 0)} transactions
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(56, 203, 137, 0.08)', borderRadius: 1, border: '1px solid rgba(56, 203, 137, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#38cb89' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Total Purchases
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {trendsData.reduce((sum, day) => sum + day.purchases, 0).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                            {trendsData.reduce((sum, day) => sum + day.purchasesCount, 0)} transactions
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Line Chart */}
                    <Box sx={{ height: 200 }}>
                      <Line
                        data={dailyTrendsChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top',
                              align: 'end',
                              labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                padding: 15,
                                font: { size: 11, weight: '500' },
                                color: '#8597ac',
                              },
                            },
                            tooltip: {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                              padding: 12,
                              titleFont: { size: 12, weight: '600' },
                              bodyFont: { size: 11 },
                              bodySpacing: 6,
                              usePointStyle: true,
                              callbacks: {
                                label: function (context) {
                                  let label = context.dataset.label || '';

                                  if (label) {
                                    label += ': ';
                                  }

                                  return label + context.parsed.y.toLocaleString();
                                }
                              }
                            },
                          },
                          scales: {
                            x: {
                              grid: {
                                display: false,
                              },
                              ticks: {
                                color: '#8597ac',
                                font: { size: 10 },
                              },
                            },
                            y: {
                              beginAtZero: true,
                              grid: {
                                color: 'rgba(0, 0, 0, 0.05)',
                                drawBorder: false,
                              },
                              ticks: {
                                color: '#8597ac',
                                font: { size: 10 },
                                callback: function (value) {
                                  return value.toLocaleString();
                                }
                              },
                            },
                          },
                          interaction: {
                            mode: 'index',
                            intersect: false,
                          },
                        }}
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="TRANSACTION SUMMARY"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                action={
                  <>
                    <IconButton size="small" onClick={handleSummaryMenuClick}>
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={summaryAnchorEl}
                      open={Boolean(summaryAnchorEl)}
                      onClose={handleSummaryMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <MenuItem
                        onClick={() => handleSummaryPeriodChange('7')}
                        selected={summaryPeriod === '7'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        7 Days
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleSummaryPeriodChange('14')}
                        selected={summaryPeriod === '14'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        14 Days
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleSummaryPeriodChange('30')}
                        selected={summaryPeriod === '30'}
                        sx={{ fontSize: '0.875rem', minWidth: 120 }}
                      >
                        1 Month
                      </MenuItem>
                    </Menu>
                  </>
                }
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 2.5 }}>
                  Complete overview of transactions over the last {summaryPeriod === '7' ? '7 days' : summaryPeriod === '14' ? '14 days' : '30 days'}
                </Typography>

                {summaryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : (
                  <>

                    {/* Summary Cards Grid */}
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                      {/* Sales Card */}
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(62, 128, 255, 0.08)', borderRadius: 1, border: '1px solid rgba(62, 128, 255, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3e80ff' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Sales
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {transactionSummary ? transactionSummary.amounts.sales.toLocaleString() : 0}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                              {transactionSummary ? transactionSummary.counts.sales.toLocaleString() : 0} transactions
                            </Typography>
                            <Chip
                              label={`${transactionSummary ? transactionSummary.distribution.sales : 0}%`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#3e80ff', color: 'white' }}
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Purchases Card */}
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(56, 203, 137, 0.08)', borderRadius: 1, border: '1px solid rgba(56, 203, 137, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#38cb89' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Purchases
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {transactionSummary ? transactionSummary.amounts.purchases.toLocaleString() : 0}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                              {transactionSummary ? transactionSummary.counts.purchases.toLocaleString() : 0} transactions
                            </Typography>
                            <Chip
                              label={`${transactionSummary ? transactionSummary.distribution.purchases : 0}%`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#38cb89', color: 'white' }}
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Credit Notes Card */}
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(255, 91, 91, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 91, 91, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff5b5b' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Credit Notes
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {transactionSummary ? transactionSummary.amounts.creditNotes.toLocaleString() : 0}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                              {transactionSummary ? transactionSummary.counts.creditNotes.toLocaleString() : 0} transactions
                            </Typography>
                            <Chip
                              label={`${transactionSummary ? transactionSummary.distribution.creditNotes : 0}%`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#ff5b5b', color: 'white' }}
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Debit Notes Card */}
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'rgba(255, 171, 0, 0.08)', borderRadius: 1, border: '1px solid rgba(255, 171, 0, 0.2)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffab00' }} />
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500 }}>
                              Debit Notes
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#031b4e', fontWeight: 600, fontSize: '1.1rem', mb: 0.25 }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            /> {transactionSummary ? transactionSummary.amounts.debitNotes.toLocaleString() : 0}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.65rem' }}>
                              {transactionSummary ? transactionSummary.counts.debitNotes.toLocaleString() : 0} transactions
                            </Typography>
                            <Chip
                              label={`${transactionSummary ? transactionSummary.distribution.debitNotes : 0}%`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#ffab00', color: 'white' }}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Total Summary Bar */}
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e6ed' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500, display: 'block' }}>
                            Total Transaction Value
                          </Typography>
                          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#031b4e', fontWeight: 700, fontSize: '1.5rem' }}>
                            <img
                              src={image1}
                              alt=""
                              width={15}
                              height={15}
                            />
                            {transactionSummary ? transactionSummary.totalAmount.toLocaleString() : 0}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 500, display: 'block' }}>
                            Total Transactions
                          </Typography>
                          <Typography variant="h5" sx={{ color: '#031b4e', fontWeight: 700, fontSize: '1.5rem' }}>
                            {transactionSummary ? (transactionSummary.counts.sales + transactionSummary.counts.purchases + transactionSummary.counts.creditNotes + transactionSummary.counts.debitNotes).toLocaleString() : 0}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Distribution Bar */}
                      <Box sx={{ position: 'relative', height: 12, bgcolor: '#e0e6ed', borderRadius: 6, overflow: 'hidden' }}>
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          height: '100%',
                          width: `${transactionSummary ? transactionSummary.distribution.sales : 0}%`,
                          bgcolor: '#3e80ff',
                          transition: 'width 0.3s ease'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: `${transactionSummary ? transactionSummary.distribution.sales : 0}%`,
                          height: '100%',
                          width: `${transactionSummary ? transactionSummary.distribution.purchases : 0}%`,
                          bgcolor: '#38cb89',
                          transition: 'width 0.3s ease'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: `${transactionSummary ? parseFloat(transactionSummary.distribution.sales) + parseFloat(transactionSummary.distribution.purchases) : 0}%`,
                          height: '100%',
                          width: `${transactionSummary ? transactionSummary.distribution.creditNotes : 0}%`,
                          bgcolor: '#ff5b5b',
                          transition: 'width 0.3s ease'
                        }} />
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          height: '100%',
                          width: `${transactionSummary ? transactionSummary.distribution.debitNotes : 0}%`,
                          bgcolor: '#ffab00',
                          transition: 'width 0.3s ease'
                        }} />
                      </Box>
                    </Paper>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Customers and Sales Activity Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="Recent Sales"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 3 }}>
                  Recent sales invoices showing customer details, amounts, and payment status
                </Typography>
                {recentSales.map((sale, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                    <Avatar sx={{
                      bgcolor: index % 5 === 0 ? '#3e80ff' :
                        index % 5 === 1 ? '#ff5b5b' :
                          index % 5 === 2 ? '#38cb89' :
                            index % 5 === 3 ? '#ffab00' :
                              '#6259ca',
                      width: 40,
                      height: 40,
                      mr: 2
                    }}>
                      {sale.customerName.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#031b4e' }}>
                          {sale.customerName}
                        </Typography>
                        <Chip
                          label={sale.paymentStatus}
                          size="small"
                          sx={{
                            bgcolor: `${sale.color}20`,
                            color: sale.color,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                            borderRadius: '3px',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: '#8597ac' }}>
                          {sale.id} • {sale.date}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#031b4e', fontWeight: 600 }}>
                          <img
                            src={image1}
                            alt=""
                            width={15}
                            height={15}
                          />  {sale.amount.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="SALES OVERVIEW"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 3 }}>
                  Track your sales performance and monitor key metrics for better business insights
                </Typography>
                {salesActivity.map((activity, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                    <Avatar sx={{
                      bgcolor: activity.color,
                      width: 40,
                      height: 40,
                      mr: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}>
                      {activity.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#031b4e' }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8597ac' }}>
                          {activity.time}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#8597ac', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {typeof activity.value === 'string' && /SAR/i.test(activity.value) ? (
                          <>
                            <img src={image1} alt="SAR" width={15} height={15} />
                            <span>{activity.value.replace(/SAR/i, '').trim()}</span>
                          </>
                        ) : (
                          activity.value
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Daily Trends and Top Products Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="VAT ORDER STATUS"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                action={
                  <IconButton size="small">
                    <MoreVert fontSize="small" />
                  </IconButton>
                }
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 2 }}>
                  Order Status and Tracking. Track your order from ship date to arrival. To begin, enter your order number.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#031b4e' }}>
                      {orderStatusData.success.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3e80ff', mr: 1 }} />
                      <Typography variant="caption" sx={{ color: '#8597ac' }}>Success</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#031b4e' }}>
                      {orderStatusData.pending.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffab00', mr: 1 }} />
                      <Typography variant="caption" sx={{ color: '#8597ac' }}>Pending</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#031b4e' }}>
                      {orderStatusData.failed.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff5b5b', mr: 1 }} />
                      <Typography variant="caption" sx={{ color: '#8597ac' }}>Failed</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ height: 220 }}>
                  <Bar
                    data={monthlyData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false,
                        },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          padding: 10,
                          titleFont: { size: 12 },
                          bodyFont: { size: 12 },
                        },
                      },
                      scales: {
                        x: {
                          grid: {
                            display: false,
                          },
                          ticks: {
                            color: '#8597ac',
                            font: { size: 10 },
                          },
                        },
                        y: {
                          grid: {
                            color: 'rgba(0, 0, 0, 0.05)',
                          },
                          ticks: {
                            color: '#8597ac',
                            font: { size: 10 },
                          },
                        },
                      },
                      barPercentage: 0.6,
                      categoryPercentage: 0.7,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={1} sx={{ borderRadius: 1, height: '100%' }}>
              <CardHeader
                title="YOUR TOP PRODUCTS"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 3 }}>
                  Sales performance revenue based by products
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ mb: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ border: 0, py: 1, fontWeight: 600, color: '#8597ac', fontSize: '0.75rem' }}>PRODUCT</TableCell>
                        <TableCell sx={{ border: 0, py: 1, fontWeight: 600, color: '#8597ac', fontSize: '0.75rem' }}>GROUP</TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 1, fontWeight: 600, color: '#8597ac', fontSize: '0.75rem' }}>QTY</TableCell>
                        <TableCell align="right" sx={{ border: 0, py: 1, fontWeight: 600, color: '#8597ac', fontSize: '0.75rem' }}>REVENUE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topProducts.map((product, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell component="th" scope="row" sx={{ border: 0, py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#031b4e', fontSize: '0.875rem' }}>
                              {product.productName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#8597ac', fontSize: '0.7rem' }}>
                              {product.productCode}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ border: 0, py: 1.5 }}>
                            <Chip
                              label={product.group}
                              size="small"
                              sx={{
                                bgcolor: '#3e80ff20',
                                color: '#3e80ff',
                                fontWeight: 500,
                                fontSize: '0.7rem',
                                height: 22
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#031b4e' }}>
                              {product.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#38cb89' }}>
                              <img
                                src={image1}
                                alt=""
                                width={15}
                                height={15}
                              /> {product.revenue}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Earnings Section */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Card elevation={1} sx={{ borderRadius: 1 }}>
              <CardHeader
                title="YOUR MOST RECENT EARNINGS"
                titleTypographyProps={{
                  variant: 'subtitle1',
                  sx: { fontSize: '0.875rem', fontWeight: 600, color: '#031b4e', textTransform: 'uppercase' }
                }}
                sx={{ p: 3, pb: 1 }}
              />
              <CardContent sx={{ p: 3, pt: 1 }}>
                <Typography variant="body2" sx={{ color: '#8597ac', mb: 3 }}>
                  This is your most recent earnings for today's date.
                </Typography>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f9f9f9' }}>
                        <TableCell sx={{ fontWeight: 600, color: '#031b4e', fontSize: '0.8rem' }}>DATE</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#031b4e', fontSize: '0.8rem' }}>SALES COUNT</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, color: '#031b4e', fontSize: '0.8rem' }}>EARNINGS</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#031b4e', fontSize: '0.8rem' }}>TAX</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentEarnings.map((earning, index) => (
                        <TableRow key={index} sx={{
                          bgcolor: index % 2 === 0 ? '#f9f9f9' : 'white',
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}>
                          <TableCell sx={{ border: 0, py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#031b4e' }}>
                              {earning.date}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ border: 0, py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#031b4e' }}>
                              {earning.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ border: 0, py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#38cb89', fontWeight: 600 }}>
                              {earning.earnings}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 0, py: 2 }}>
                            <Typography variant="body2" sx={{ color: '#ff5b5b', fontWeight: 500 }}>
                              {earning.tax}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', py: 3, color: '#8597ac' }}>
          <Typography variant="body2">

          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;